import {useEffect, useRef, useState} from 'react'
import {useApolloClient, useMutation} from '@apollo/client/react'
import {v4 as uuid} from 'uuid'

import {ABANDON_UPLOAD, COMPLETE_UPLOAD, CREATE_UPLOAD_PRESIGNER, PRESIGN_UPLOAD_PARTS} from '../../graphql/mutations/uploads'
import {FOLDERS} from '../../graphql/queries/folders'
import {LOCKED} from '../../graphql/queries/locked'
import {STORAGE} from '../../graphql/queries/storage'
import {UPLOAD, UPLOADS} from '../../graphql/queries/uploads'
import {NOT_FOUND, says} from '../errorTools'
import {getsThumbnail} from '../fileTools'

const UNKNOWN_TYPE = 'application/octet-stream'

// The worker reports by status alone, so how soon a step shows is how often it is asked.
const POLL_INTERVAL = 3000

// What each status the API reports means to someone following an upload. "encrypted" is the moment
// between sealing the file and storing the sealed copy, which is still the encrypting step to them.
const SERVER_PHASES = {
  uploading: 'waiting',
  scanning: 'scanning',
  encrypting: 'encrypting',
  encrypted: 'encrypting',
  uploaded: 'done',
  virus: 'blocked',
  failed: 'failed',
  resealing: 'encrypting'
}

export const SETTLED_PHASES = ['done', 'blocked', 'failed']

const ON_SERVER = ['waiting', 'scanning', 'encrypting']

// The worker makes a file's thumbnail after securing it, so one that gets a thumbnail is asked after
// for this much longer: enough for the slowest it allows itself, listening through a recording.
const THUMBNAIL_WAIT = 4 * 60 * 1000

// A file sent in parts has this many of them going out at once, each worker presigning a few at a
// time. Each url lasts ten minutes, so a batch is kept small enough to be sent well within that on
// a slow connection shared with the other workers.
const PARTS_IN_FLIGHT = 4
const PARTS_PER_PRESIGN = 4

// A part that fails is sent again, to a freshly presigned url in case the last one ran out, after
// one second, then two, then four.
const PART_ATTEMPTS = 4
const FIRST_RETRY_DELAY = 1000

/**
 * Until when a secured file is still asked after for its thumbnail, or null once there is no more
 * reason to: it has its thumbnail, isn't a kind that gets one, isn't secured, or has waited its time.
 */
export const thumbnailDeadline = ({name, awaitThumbnailUntil}, phase, upload) => {
  if (phase !== 'done' || upload.thumbnail || !getsThumbnail(name)) return null
  if (!awaitThumbnailUntil) return Date.now() + THUMBNAIL_WAIT

  return Date.now() < awaitThumbnailUntil ? awaitThumbnailUntil : null
}

const isFollowed = ({phase, awaitThumbnailUntil}) => ON_SERVER.includes(phase) || Boolean(awaitThumbnailUntil)

const wait = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds))

// XMLHttpRequest rather than fetch: it is the only way a browser reports how much of a body has
// gone out.
const putWithProgress = (url, file, onProgress) => new Promise((resolve, reject) => {
  const request = new XMLHttpRequest()

  request.open('PUT', url)
  request.upload.onprogress = event => event.lengthComputable && onProgress(event.loaded / event.total)
  request.onload = () => (
    request.status >= 200 && request.status < 300 ?
      resolve() :
      reject(new Error(`Storage refused ${file.name} (${request.status})`))
  )
  request.onerror = () => reject(new Error(`Storage could not be reached for ${file.name}`))
  request.send(file)
})

const partsOf = (size, partSize) => Array.from({length: Math.max(1, Math.ceil(size / partSize))}, (part, index) => ({
  number: index + 1,
  start: index * partSize,
  end: Math.min(size, (index + 1) * partSize)
}))

// Named for the file it came from, as storage's refusals are told by name. Wrapping a slice copies
// no bytes.
const partFile = (file, start, end) => new File([file.slice(start, end)], file.name)

const batchesOf = (parts, size) => Array.from({length: Math.ceil(parts.length / size)}, (batch, index) => (
  parts.slice(index * size, (index + 1) * size)
))

/**
 * Sends a file in parts of "partSize", several at once, each to a url "presignParts" hands out for
 * a list of part numbers as a map from number to url. Storage puts the parts together when the API
 * is told the file is complete, so this only has to get every part there.
 *
 * A part that fails is retried on its own, which is the point of sending in parts: a dropped
 * connection costs that part, not the file. Once a part has used up its attempts the upload has
 * failed, and the other workers stop taking new parts; abandoning the upload throws away what they
 * already sent.
 */
export const putInParts = async (file, partSize, presignParts, onProgress) => {
  const parts = partsOf(file.size, partSize)
  const sentOfEach = new Map()
  const batches = batchesOf(parts, PARTS_PER_PRESIGN)
  let failed = false

  const report = (number, sent) => {
    sentOfEach.set(number, sent)
    onProgress([...sentOfEach.values()].reduce((total, bytes) => total + bytes, 0) / file.size)
  }

  const putPart = async ({number, start, end}, presignedUrl) => {
    let url = presignedUrl

    for (let attempt = 1; ; attempt += 1) {
      try {
        url ??= (await presignParts([number])).get(number)

        return await putWithProgress(url, partFile(file, start, end), sent => report(number, sent * (end - start)))
      } catch (error) {
        report(number, 0)

        if (attempt === PART_ATTEMPTS) throw error

        url = null
        await wait(FIRST_RETRY_DELAY * 2 ** (attempt - 1))
      }
    }
  }

  // A batch whose urls could not be had leaves each part to ask for its own as it retries.
  const sendBatches = async () => {
    while (batches.length && !failed) {
      const batch = batches.shift()
      const urls = await presignParts(batch.map(({number}) => number)).catch(() => new Map())

      for (const part of batch) {
        if (failed) return

        await putPart(part, urls.get(part.number))
      }
    }
  }

  await Promise.all(Array.from({length: PARTS_IN_FLIGHT}, () => sendBatches().catch(error => {
    failed = true

    throw error
  })))
}

/**
 * Sends files to storage and follows each one until it is secured, for the upload tray to show.
 *
 * Each file is handed over as "{file, folderId, path, locked}": "folderId" is the folder it goes in,
 * or null for the top of the drive, and "path" the folders below that to put it in, "/" between
 * them, which the API makes where they are missing — how a folder chosen from disk keeps its shape.
 * "locked" puts it in Locked instead, which must be open: "folderId" is then one of its folders, or
 * null for its top. Every file is presigned before any of
 * them is sent: the API records an upload as it presigns it, so the listings can show what is on its
 * way while the bytes are still going out, rather than after the slowest file lands.
 *
 * An upload passes through the browser first and the API after: "queued" while it is presigned,
 * "uploading" while its bytes go out — at the end of which the API is told, so its worker starts on
 * the file at once rather than at its first scheduled look — then whatever the worker reports as it scans, encrypts
 * and stores the sealed copy — learned by asking, every few seconds, for as long as anything is
 * still with the worker. It ends "done", "blocked" or "failed". A file that is "done" is asked after a
 * while longer if it gets a thumbnail, which the worker makes once the file is secured; the answer
 * that carries it puts it in the listings, through the cache.
 *
 * A file storage refuses is taken back before the listings refresh, so it leaves no row stuck in
 * "uploading". That is best effort: if the take-back is lost too, the API's own job discards the
 * upload once it has waited long enough for a file that never came, and asking after it then finds
 * nothing — which is a failure too.
 */
const useUploads = () => {
  const client = useApolloClient()
  const [requestPresigner] = useMutation(CREATE_UPLOAD_PRESIGNER)
  const [abandonUpload] = useMutation(ABANDON_UPLOAD)
  const [completeUpload] = useMutation(COMPLETE_UPLOAD)
  const [requestPartUrls] = useMutation(PRESIGN_UPLOAD_PARTS)
  const [entries, setEntries] = useState([])
  const latestEntries = useRef(entries)

  useEffect(() => {
    latestEntries.current = entries
  })

  // Progress events arrive far faster than the rounded percentage moves, so an update that changes
  // nothing leaves the list as it was rather than drawing it again.
  const revise = (key, changes) => setEntries(current => {
    const entry = current.find(candidate => candidate.key === key)

    if (!entry || Object.keys(changes).every(field => entry[field] === changes[field])) return current

    return current.map(candidate => (candidate.key === key ? {...candidate, ...changes} : candidate))
  })

  const refreshListings = () => client.refetchQueries({include: [FOLDERS, LOCKED, STORAGE, UPLOADS]})

  const presign = ({file, folderId = null, path = null, locked = false}) => requestPresigner({
    variables: {
      input: {filename: file.name, fileType: file.type || UNKNOWN_TYPE, folderId, path, filesize: file.size, ...(locked && {locked})}
    }
  }).then(({data}) => data.createUploadPresigner)

  const presignParts = id => partNumbers => requestPartUrls({variables: {id, partNumbers}})
    .then(({data}) => new Map(data.presignUploadParts.map(({partNumber, presignedUrl}) => [partNumber, presignedUrl])))

  // The API sends a large file in parts, and says so by giving it a part size rather than a url.
  const send = ({id, presignedUrl, partSize}, file, onProgress) => (
    presignedUrl ?
      putWithProgress(presignedUrl, file, onProgress) :
      putInParts(file, partSize, presignParts(id), onProgress)
  )

  const transfer = async (key, presigner, file) => {
    const {id} = presigner

    try {
      await send(presigner, file, sent => revise(key, {progress: Math.round(sent * 100)}))
    } catch {
      await abandonUpload({variables: {id}}).catch(() => {})
      revise(key, {phase: 'failed'})

      return
    }

    revise(key, {phase: 'waiting', progress: 100})
    // Not waited on: it only brings the worker forward, and if it is lost the scheduled run
    // secures the file all the same.
    completeUpload({variables: {id}}).catch(() => {})
  }

  const upload = async items => {
    if (!items.length) return

    const added = items.map(({file, folderId = null, path = null, locked = false}) => (
      {key: uuid(), name: file.name, size: file.size, folderId, path, locked, phase: 'queued', progress: 0}
    ))

    setEntries(current => [...added, ...current])

    const presigned = await Promise.allSettled(items.map(presign))

    presigned.forEach((result, index) => revise(added[index].key, (
      result.status === 'fulfilled' ? {uploadId: result.value.id, phase: 'uploading'} : {phase: 'failed'}
    )))
    refreshListings()

    await Promise.all(presigned.map((result, index) => (
      result.status === 'fulfilled' && transfer(added[index].key, result.value, items[index].file)
    )))
    refreshListings()
  }

  const follow = entry => client.query({query: UPLOAD, variables: {id: entry.uploadId}, fetchPolicy: 'network-only'})
    .then(({data}) => {
      const phase = SERVER_PHASES[data.upload.status]

      revise(entry.key, {phase, awaitThumbnailUntil: thumbnailDeadline(entry, phase, data.upload)})

      if (SETTLED_PHASES.includes(phase) && phase !== entry.phase) refreshListings()
    })
    // Anything short of the upload being gone is asked about again on the next round.
    .catch(error => says(error, NOT_FOUND) && revise(entry.key, {phase: 'failed'}))

  const following = entries.some(isFollowed)

  // Keyed on whether anything is still followed, not on the entries themselves, so progress events
  // don't restart the clock. The interval reads the entries through the ref for the same reason.
  useEffect(() => {
    if (!following) return

    const timer = setInterval(() => latestEntries.current.filter(isFollowed).forEach(follow), POLL_INTERVAL)

    return () => clearInterval(timer)
  }, [following])

  return {upload, uploads: entries, dismissUploads: () => setEntries([])}
}

export default useUploads
